// --- Helper Functions for Calculating Out-of-Pocket Contribution ---
/**
 * Calculates the standard out-of-pocket contribution for a health course.
 * Assumption: First course in the calendar year.
 * @param courseCost - The total cost of the course.
 * @param reimbursementPercentage - The percentage of costs the provider reimburses (e.g., 0.80 for 80%).
 * @param maxReimbursement - The maximum amount the provider reimburses for this course (can be per course or per year, depending on the provider and assumption).
 * @returns The amount the insured person has to pay out-of-pocket.
 */
function calculateStandardContribution(courseCost, reimbursementPercentage, maxReimbursement) {
    const potentialReimbursement = courseCost * reimbursementPercentage;
    // Reimbursement cannot exceed cost or the specified maximum
    const actualReimbursement = Math.min(potentialReimbursement, maxReimbursement, courseCost);
    const outOfPocketContribution = courseCost - actualReimbursement;
    // Ensure the contribution is not negative
    return Math.max(0, outOfPocketContribution);
}
/**
 * Calculates the out-of-pocket contribution for a health course when the provider
 * reimburses 100% up to a maximum amount.
 * Assumption: First course in the calendar year.
 * @param courseCost - The total cost of the course.
 * @param maxReimbursement - The maximum amount the provider reimburses for this course.
 * @returns The amount the insured person has to pay out-of-pocket.
 */
function calculateContribution100Percent(courseCost, maxReimbursement) {
    const actualReimbursement = Math.min(courseCost, maxReimbursement);
    const outOfPocketContribution = courseCost - actualReimbursement;
    return Math.max(0, outOfPocketContribution);
}
/**
 * Specific calculation for HEK-Hanseatische Krankenkasse (tiered subsidy).
 * Assumption: First course in the calendar year.
 * @param courseCost - The cost of the course.
 * @returns The out-of-pocket contribution.
 */
function calculateContributionHEK(courseCost) {
    let actualReimbursement;
    if (courseCost >= 300) {
        actualReimbursement = 250;
    }
    else if (courseCost > 150) {
        actualReimbursement = 100;
    }
    else { // courseCost <= 150
        actualReimbursement = 75;
    }
    // Reimbursement cannot exceed the course cost
    actualReimbursement = Math.min(actualReimbursement, courseCost);
    const outOfPocketContribution = courseCost - actualReimbursement;
    return Math.max(0, outOfPocketContribution);
}
/**
 * Specific calculation for IKK Südwest (with minimum reimbursement).
 * Assumption: First course in the calendar year, adult rate.
 * @param courseCost - The cost of the course.
 * @returns The out-of-pocket contribution.
 */
function calculateContributionIkkSuedwest(courseCost) {
    let actualReimbursement;
    const maxReimbursementPerCourse = 150;
    const minReimbursementForHigherCosts = 75;
    const reimbursementPercentage = 0.80;
    if (courseCost < minReimbursementForHigherCosts) {
        // If cost < 75€, full reimbursement
        actualReimbursement = courseCost;
    }
    else {
        // If cost >= 75€
        const potentialReimbursement = courseCost * reimbursementPercentage;
        // Reimbursement is at least 75€, but max 150€ and max the 80%
        actualReimbursement = Math.max(minReimbursementForHigherCosts, potentialReimbursement);
        actualReimbursement = Math.min(actualReimbursement, maxReimbursementPerCourse);
    }
    // Reimbursement cannot exceed the course cost (safety check)
    actualReimbursement = Math.min(actualReimbursement, courseCost);
    const outOfPocketContribution = courseCost - actualReimbursement;
    return Math.max(0, outOfPocketContribution);
}
/**
 * Specific calculation for BARMER (Online vs. On-site).
 * Assumption: First course in the calendar year.
 * @param courseCost - The cost of the course.
 * @param isOnline - Indicates if the course is online (Default: false).
 * @returns The out-of-pocket contribution.
 */
function calculateContributionBarmer(courseCost, isOnline = true) {
    const subsidy = isOnline ? 100 : 75;
    const actualReimbursement = Math.min(courseCost, subsidy);
    const outOfPocketContribution = courseCost - actualReimbursement;
    return Math.max(0, outOfPocketContribution);
}
/**
 * Function for providers where the calculation logic is too vague.
 * @param courseCost - The cost of the course.
 * @returns Always returns null.
 */
function calculationNotPossible(courseCost) {
    return null;
}
window.healthInsuranceProviderData = {
    'AOK Bayern': {
        calculateContribution: (cost) => calculateContribution100Percent(cost, 150),
        description: 'Die AOK Bayern bezuschusst die Kurse von Fremdanbietern mit bis zu 300 EUR pro Jahr. Erstattet werden 100 % der Kosten bis zu 150 EUR pro Kurs. Versicherte der AOK Bayern können maximal 2 Kurse pro Jahr auf Kosten der AOK Bayern in Anspruch nehmen.'
    },
    'AOK Hessen': {
        calculateContribution: (cost) => calculateContribution100Percent(cost, 150),
        description: 'Wer bei einem externen, zertifizierten Anbieter in den Kategorien Ernährung, Bewegung, Entspannung und Umgang mit Suchtmitteln einen Kurs bucht, erhält 100 %, höchstens bis zu 150 EUR je Kurs bzw. für maximal zwei Kurse jährlich bis zu 300 Euro erstattet.'
    },
    'AOK Niedersachsen': {
        // Assumption: Annual maximum (150€) applies as the limit for the first course
        calculateContribution: (cost) => calculateStandardContribution(cost, 0.85, 150),
        description: 'Bei Präventionskurse von anderen Anbietern erstattet die AOK Niedersachsen 85 Prozent der Kursgebühr, maximal 150 Euro pro Jahr. Pro Kalenderjahr fördert die AOK bis zu 2 Gesundheitskurse.'
    },
    'AOK NordWest': {
        calculateContribution: (cost) => calculateStandardContribution(cost, 0.80, 250),
        description: 'Gerne beteiligt sich die AOK NordWest auch an den Kosten von Kurs-Angeboten Dritter, wenn diese die notwendigen Qualitätsstandards erfüllen. Die AOK erstattet pro Jahr für zwei Kurse 80 % der Kosten – maximal 250 Euro pro Kurs, Gesamtanspruch 500 Euro pro Jahr.'
    },
    'AOK PLUS': {
        // Assumption: "kostenlos" (free) means 100% reimbursement with no practical limit for typical course costs
        calculateContribution: (cost) => calculateContribution100Percent(cost, 9999), // Using a very high limit as an assumption
        description: 'Versicherte können an bis zu 2 Gesundheitskursen pro Kalenderjahr kostenlos teilnehmen.'
    },
    'AOK Rheinland-Pfalz/Saarland': {
        // Assumption: Annual maximum (400€) applies as the limit for the first course
        calculateContribution: (cost) => calculateContribution100Percent(cost, 400),
        description: 'Die AOK Rheinland-Pfalz/Saarland beteiligt sich an den Kosten von Kurs-Angeboten Dritter, wenn diese die notwendigen Qualitätsstandards erfüllen. Erstattet werden 100 % der Kursgebühren bis zu 400 Euro pro Kalenderjahr für zwei Gesundheitskurse.'
    },
    'BAHN-BKK': {
        calculateContribution: (cost) => calculateStandardContribution(cost, 0.80, 150),
        description: 'Bei regelmäßiger Teilnahme an Gesundheitskursen wird ein Großteil der Kosten erstattet. Kurse aus der Kursdatenbank bezuschusst die Krankenkasse zu 80 %, mit max. 150 Euro je Kurs. Die BAHN-BKK bezuschusst pro Jahr zwei anerkannte Kurse, das bedeutet man kann einen maximalen Zuschuss von 300 Euro für Gesundheitskurse pro Jahr erhalten.'
    },
    'BERGISCHE Krankenkasse': {
        // Assumption: 100% up to 560€/2 = 280€ per course
        calculateContribution: (cost) => calculateContribution100Percent(cost, 280),
        description: 'Die BERGISCHE beteiligt sich mit bis zu 560 Euro jährlich für die Teilnahme an zwei zertifizierten Sport- und Gesundheitskursen aus den Handlungsfeldern Bewegung, Ernährung, Stressbewältigung oder Suchtprävention.'
    },
    'BKK Akzo Nobel Bayern': {
        // Assumption: Annual maximum (300€) applies as the limit for the first course
        calculateContribution: (cost) => calculateStandardContribution(cost, 0.90, 300),
        description: 'Die BKK Akzo Nobel Bayern fördert gesundheitsbewusstes Verhalten mit einem Zuschuss von 90% der Kursgebühr, maximal aber bis zu 300 EUR pro Kalenderjahr für 2 Kurse.'
    },
    'BKK exklusiv': {
        calculateContribution: (cost) => calculateContribution100Percent(cost, 200),
        description: 'Für Leistungen von Fremdanbietern wird, sofern sie den im „Leitfaden Prävention“ aufgeführten Qualitätskriterien genügen, bei Vorlage einer Teilnahmebestätigung ein einmaliger Finanzierungszuschuss in Höhe von 100% der entstandenen Kosten, maximal bis zu 200 Euro je Maßnahme gewährt. Je Kalenderjahr können zwei Maßnahmen erstattet werden, insgesamt also bis zu 400 Euro.'
    },
    'BKK Faber-Castell & Partner': {
        // Assumption: Annual maximum (250€) applies as the limit for the first course
        calculateContribution: (cost) => calculateContribution100Percent(cost, 250),
        description: 'Für zertifizierte Präventionskurse vor Ort oder online von Fremdanbietern wird, bei Vorlage einer Teilnahmebestätigung einschließlich eines Nachweises über die Teilnahme an mindestens 80% der Kurseinheiten ein einmaliger Finanzierungszuschuss in Höhe von 100% der entstandenen Kosten, maximal aber 250 Euro je Kalenderjahr gewährt.'
    },
    'BKK Freudenberg': {
        // Assumption: 90% up to 400€/2 = 200€ per course
        calculateContribution: (cost) => calculateStandardContribution(cost, 0.90, 200),
        description: 'Für Leistungen von Fremdanbietern wird eine Finanzierungszuschuss in Höhe von 90% der entstandenen Kosten für 2 Kurse/Jahr gewährt. Der Gesamtzuschuss beträgt 400 Euro.'
    },
    'BKK Herkules': {
        // Assumption: Annual maximum (560€) applies as the limit for the first course
        calculateContribution: (cost) => calculateContribution100Percent(cost, 560),
        description: 'Für zertifizierte Präventionskurse bietet die BKK Herkules jedes Jahr ein Budget von 560 Euro, in dem jeder Kurs mit 100 % bezuschusst wird.'
    },
    'BKK PFAFF': {
        // Assumption: 100% up to 150€ per course
        calculateContribution: (cost) => calculateContribution100Percent(cost, 150),
        description: 'Pro Kalenderjahr stehen bei der BKK PFAFF für bis zu zwei Kurse bis zu 150 Euro je Kurs zur Verfügung.'
    },
    'BKK Pfalz': {
        calculateContribution: (cost) => calculateStandardContribution(cost, 0.90, 150),
        description: 'Für Leistungen von Fremdanbietern erhält der Versicherte von der BKK Pfalz einen Zuschuss in Höhe von 90% der entstandenen Kosten, maximal aber 150 Euro je Maßnahme. Der Zuschuss wird zu maximal 2 Kursen im Kalenderjahr gezahlt.'
    },
    'BKK ProVita': {
        // Assumption: Annual maximum (236€) applies as the limit for the first course
        calculateContribution: (cost) => calculateStandardContribution(cost, 0.80, 236),
        description: 'Für Leistungen von Fremdanbietern wird bei Vorlage einer Teilnahmebestätigung ein Finanzierungszuschuss in Höhe von 80 % der entstandenen Kosten, maximal 236 Euro pro Kalenderjahr. Die BKK ProVita fördert maximal zwei Maßnahmen pro Versichertem je Kalenderjahr.'
    },
    'BKK Public': {
        // Assumption: Annual maximum (155€) applies as the limit for the first course (adult rate)
        calculateContribution: (cost) => calculateStandardContribution(cost, 0.85, 155),
        description: 'Präventionskurse werden in Höhe von 85 % der Kursgebühren bei Erwachsenen bzw. 100 % bei Kindern bis zum vollendeten 18. Lebensjahr bis zu einem Höchstbetrag von 155 EUR im Kalenderjahr bezahlt. Die Förderung durch die BKK ist auf max. zwei Kurse pro Versicherten und Kalenderjahr begrenzt.'
    },
    'BKK Scheufelen': {
        calculateContribution: (cost) => calculateStandardContribution(cost, 0.85, 155),
        description: 'Bis 310 Euro Zuschuss jährlich (85% der Kosten - max. 155 Euro pro Maßnahme) werden für 2 Kurse jährlich übernommen.'
    },
    'BKK VDN': {
        calculateContribution: (cost) => calculateContribution100Percent(cost, 160),
        description: 'Für 2 Kurse pro Kalenderjahr - auch zum gleichen Thema - erfolgt eine max. Erstattung in Höhe von 100 % der Kosten, maximal 160 Euro je Kurs.'
    },
    'BKK VerbundPlus': {
        calculateContribution: (cost) => calculateStandardContribution(cost, 0.90, 150),
        description: 'Bei Fremdkursen beträgt der Zuschuss 90% der Kosten, bis zu 150 Euro je Maßnahme. Pro Kalenderjahr sind 2 Kurse, also insgesamt 300 Euro, zuschussfähig.'
    },
    'BKK Wirtschaft & Finanzen': {
        // Assumption: 100% up to 600€/2 = 300€ per course
        calculateContribution: (cost) => calculateContribution100Percent(cost, 300),
        description: 'Die Kosten qualifizierter Angebote zur Gesundheitsförderung erstattet die BKK WIRTSCHAFT & FINANZEN 2 Mal im Kalenderjahr zu 100 % bis zur Gesamthöhe von 600 Euro.'
    },
    'HEK-Hanseatische Krankenkasse': {
        calculateContribution: calculateContributionHEK,
        description: 'Die Zuschüsse werden für höchstens zwei Maßnahmen innerhalb eines Kalenderjahres gewährt. Diese betragen bei einer Kursgebühr bis 150 Euro, höchstens 75 Euro, bei einer Kursgebühr über 150 Euro, höchstens 100 Euro und bei einer Kursgebühr ab 300 Euro, pauschal 250 Euro.'
    },
    'IKK Südwest': {
        calculateContribution: calculateContributionIkkSuedwest,
        description: 'Für zertifizierte Kurse von anderen Anbietern gewährt die IKK einen Zuschuss in Höhe von 80 v. H. der Kosten, maximal 150,00 Euro, mindestens 75,00 Euro je Kurs, sofern die tatsächlichen Kosten des Kurses 75,00 Euro übersteigen. Betragen die tatsächlichen Kosten des Kurses weniger als 75,00 Euro, gewährt die IKK einen Zuschuss in Höhe der tatsächlich entstandenen Kosten. Die Kostenübernahme für jeden Versicherten beträgt dabei für insgesamt zwei Kurse bis zu maximal 300,00 Euro pro Kalenderjahr. Für Kinder, Jugendliche sowie Schüler und Studenten bis zur Vollendung des 25. Lebensjahres beträgt der Zuschuss zu den Kosten 100 v. H., unter Beachtung des jeweiligen Maximalbudgets pro Kalenderjahr.'
    },
    'KKH Kaufmännische Krankenkasse': {
        calculateContribution: (cost) => calculateContribution100Percent(cost, 150),
        description: 'Die KKH bezuschusst qualitätsgeprüfte Kurse mit dem Zertifikat Deutscher Standard Prävention. Sie übernimmt zwei Mal jährlich 100 Prozent (maximal 150 €) der Kosten (bis zu 300 Euro im Jahr).'
    },
    'Mobil Krankenkasse': {
        // Assumption: 100% up to 1200€/2 = 600€ per course
        calculateContribution: (cost) => calculateContribution100Percent(cost, 600),
        description: 'Die Mobil Krankenkasse übernimmt die Kosten für Gesundheitskurse von Fremdanbietern in voller Höhe bis zu 1.200,00 Euro für bis zu zwei Präventionskurse im Kalenderjahr. Voraussetzung ist lediglich, dass der jeweilige Kurs von der Zentralen Prüfstelle Prävention (ZPP) zertifiziert und an mindestens 80% der Termine teilgenommen wurde.'
    },
    'Novitas BKK': {
        // Assumption: 100% up to 300€/2 = 150€ per course
        calculateContribution: (cost) => calculateContribution100Percent(cost, 150),
        description: 'Die Novitas BKK fördert die Gesundheit und erstattet Kosten für Gesundheitskurse mit bis zu 300 EUR jährlich (maximal zwei Kurse pro Versicherte und Kalenderjahr).'
    },
    'Pronova BKK': {
        // Assumption: Annual maximum (150€) applies as the limit for the first course
        calculateContribution: (cost) => calculateStandardContribution(cost, 0.80, 150),
        description: '150,00 € Zuschuss im Jahr (2 Gesundheitskurse á 80 % der Kurskosten).'
    },
    'SECURVITA Krankenkasse': {
        calculateContribution: (cost) => calculateContribution100Percent(cost, 150),
        description: 'Die SECURVITA Krankenkasse übernimmt die Kosten für die Gesundheitsförderung und Prävention zu 100 Prozent bis maximal 150 Euro je Kurs, zweimal im Jahr. Der Zuschuss beträgt maximal 300 Euro im Kalenderjahr.'
    },
    'TUI BKK': {
        // Assumption: Annual maximum (155€) applies as the limit for the first course (adult rate)
        calculateContribution: (cost) => calculateStandardContribution(cost, 0.85, 155),
        description: 'Präventionskurse werden in Höhe von 85 % der Kursgebühren bei Erwachsenen bzw. 100 % bei Kindern bis zum vollendeten 18. Lebensjahr bis zu einem Höchstbetrag von 155 EUR im Kalenderjahr bezahlt. Die Förderung durch die BKK ist auf max. zwei Kurse pro Versicherten und Kalenderjahr begrenzt.'
    },
    'VIACTIV Krankenkasse': {
        // Assumption: 100% up to 550€/2 = 275€ per course
        calculateContribution: (cost) => calculateContribution100Percent(cost, 275),
        description: 'Die VIACTIV Krankenkasse übernimmt 100% der Kursgebühren bis zu 550 Euro im Jahr für maximal 2 Gesundheitskurse.'
    },
    'vivida bkk': {
        // Ignores 100% for specific partners, uses standard for external providers
        calculateContribution: (cost) => calculateStandardContribution(cost, 0.80, 150),
        description: 'Die vivida bkk unterstützt Sie mit bis zu 300 Euro jährlich für zwei zertifizierte Gesundheitskurse. Dabei erstattet sie Ihnen pro Kurs 80 % der Kosten (bis zu 150 Euro pro Kurs). Voraussetzung der Kostenübernahme ist eine Mindestteilnahme von 80 %. Die Kosten der Online-Angebote unserer der Kooperationspartner Gymondo, Balloon, Fitbase, Aumio (nur für Kinder) und Sanero Medical GmbH (NichtraucherHelden) ünbernimmt die vivida bkk zu 100 %.'
    },
    'WMF BKK': {
        calculateContribution: (cost) => calculateStandardContribution(cost, 0.80, 200),
        description: 'Die WMF Betriebskrankenkasse bezuschusst zwei Präventionskurse pro Kalenderjahr mit 80% bis zu 200 Euro je Kurs. Ein Zuschuss von bis zu 400 Euro pro Kalenderjahr ist somit möglich.'
    },
    'AOK Baden-Württemberg': {
        // Description "zu einem großen Teil" (a large part) is too vague for calculation
        calculateContribution: (cost) => calculateContribution100Percent(cost, 80),
        description: 'Die Kosten für Gesundheitskurse anderer Anbieter, die entsprechende Qualitätsanforderungen erfüllen, erstattet die AOK BW zu einem großen Teil für bis zu zwei Kurse pro Kalenderjahr.'
    },
    'AOK Bremen/Bremerhaven': {
        calculateContribution: (cost) => calculateStandardContribution(cost, 0.80, 110),
        description: 'Versicherte können aus qualitätsgeprüften Gesundheitskursen wählen: die AOK Bremen/Bremerhaven erstattet nach Vorlage einer personifizierten Teilnahmebescheinigung zweimal pro Kalenderjahr 80% der Kosten bis zu einer Höhe von maximal 110 Euro pro Kurs.'
    },
    'AOK Nordost': {
        // Assumption: 100% up to 170€/2 = 85€ per course
        calculateContribution: (cost) => calculateContribution100Percent(cost, 85),
        description: 'Die Teilnahme an Fremdkursen ist möglich. Pro Kalenderjahr können max. 2 Kurse besucht werden und einen Zuschuss in Höhe von insgesamt 170 Euro erhalten.'
    },
    'AOK Rheinland/Hamburg': {
        calculateContribution: (cost) => calculateContribution100Percent(cost, 100),
        description: 'Die Teilnahme an Präventionskursen, die von der zentralen Prüfstelle Prävention auf ihre Qualität geprüft worden sind, bezuschusst die AOK Rheinland/Hamburg zu 100 %, maximal 200 Euro für zwei Gesundheitskurse je Kalenderjahr (100 Euro je Kurs).'
    },
    'AOK Sachsen-Anhalt': {
        // Assumption: 100% up to annual maximum (80€) as the limit for the first course
        calculateContribution: (cost) => calculateContribution100Percent(cost, 80),
        description: 'Die Inanspruchnahme von Gesundheitskursen oder qualitätsgesicherten Kursen anderer Anbieter wird mit bis zu 80 Euro je Versicherten je Kalenderjahr mit bis zu zwei Maßnahmen gefördert.'
    },
    'Audi BKK': {
        calculateContribution: (cost) => calculateContribution100Percent(cost, 100),
        description: '100 % der Kosten für Gesundheitskurse, die bei qualitätsgeprüften Anbietern durchgeführt werden, werden übernommen (maximal 2 Kurse je Kalenderjahr, 100 Euro je Maßnahmemax. 200 Euro insgesamt).'
    },
    'BARMER': {
        // Accepts optional { isOnline: boolean } in the second argument
        calculateContribution: (cost) => calculateContributionBarmer(cost),
        description: 'Der Regelzuschuss für Maßnahmen der individuellen Prävention (Primäre Prävention; Gesundheitsförderung) beträgt 75 € je Maßnahme die vor Ort durchgeführt wird, 100 € für online-basierte Maßnahmen. Erstattet werden max. 2 Kurse pro Jahr.'
    },
    'Bertelsmann BKK': {
        calculateContribution: (cost) => calculateStandardContribution(cost, 0.85, 80),
        description: 'Der Zuschuss pro Kurs beträgt 85 % bis 80 EUR je Kurs. Nimmt ein Versicherter der BKK an einer Präventionsmaßnahme teil, die von einer anderen gesetzlichen Krankenkasse angeboten wird, hat der Versicherte einen Anspruch auf Übernahme der vollen Kosten bis zu einem Höchstwert von 150 EUR je Kurs.'
    },
    'BIG direkt gesund': {
        // Assumption: 100% up to 120€ per course
        calculateContribution: (cost) => calculateContribution100Percent(cost, 120),
        description: 'Für zertifizierte Fremdanbieter wird ein Zuschuss von maximal 120 Euro je Maßnahme gezahlt. Die Leistung ist auf maximal zwei Kurse pro Kalenderjahr begrenzt.'
    },
    'BKK Diakonie': {
        calculateContribution: (cost) => calculateStandardContribution(cost, 0.80, 100),
        description: 'Für Leistungen im Rahmen der Primärprävention beteiligt sich die BKK Diakonie in Höhe von 80% der entstandenen Kosten bis max. 100,00 Euro. Die Förderung ist auf max. 2 Kurse pro Versicherten und Kalenderjahr begrenzt.'
    },
    'BKK DürkoppAdler': {
        // Assumption: 100% up to 100€ per course
        calculateContribution: (cost) => calculateContribution100Percent(cost, 100),
        description: 'Die BKK_DürkoppAdler erstattet die Kosten für zertifizierte Präventionskurse bis zu 200 Euro pro Kalenderjahr (max. 100 Euro pro Kurs, max. 2 Kurse pro Kalenderjahr) bei einer regelmäßigen Teilnahme von mindestens 80 %.'
    },
    'BKK EUREGIO': {
        // Assumption: 100% up to annual maximum (200€) as the limit for the first course
        calculateContribution: (cost) => calculateContribution100Percent(cost, 200),
        description: 'Leistungen von Fremdanbietern werden maximal bis zu einem Betrag von 200 Euro je Kalenderjahr und je Versicherten gewährt.'
    },
    'BKK firmus': {
        calculateContribution: (cost) => calculateStandardContribution(cost, 0.90, 103),
        description: 'Die BKK firmus erstattet ihren Kunden 90 % der Kosten an Gesundheitskursen (max. 2 Kurse pro Jahr mit max. 103 Euro/Kurs).'
    },
    'BKK GILDEMEISTER SEIDENSTICKER': {
        // Assumption: 100% up to 75€ per course
        calculateContribution: (cost) => calculateContribution100Percent(cost, 75),
        description: 'Pro Jahr übernimmt die BKK GILDEMEISTER SEIDENSTICKER bei regelmäßiger Teilnahme (mind. 80 % der Termine) die Kosten für max. 2 Gesundheitskurse mit einem Höchstbetrag von bis zu 75 Euro je Kurs und Handlungsfeld. Der gleiche Gesundheitskurs kann auch zweimal im Jahr durchgeführt werden kann.'
    },
    'BKK Linde': {
        calculateContribution: (cost) => calculateContribution100Percent(cost, 100),
        description: 'Zu den entstandenen Kosten übernimmt die BKK Linde für bis zu 2 Präventionsmaßnahmen einen Zuschuss in Höhe von 100 %, max. 100 EUR pro Kurs und Jahr. Die Teilnahme an Präventionskursen (online und/oder präsenz) wird im Bonusprogramm FitBonus+ mit zusätzlichen 10 Euro bonifiziert.'
    },
    'bkk melitta hmr': {
        calculateContribution: (cost) => calculateContribution100Percent(cost, 120),
        description: 'Für Leistungen von Fremdanbietern wird ein einmaliger Finanzierungszuschuss von 100% der entstandenen Kosten, max. aber 120 Euro je Maßnahme gewährt.'
    },
    'BKK mkk': {
        // Assumption: Annual maximum (400€) from health account applies as the limit for the first course
        calculateContribution: (cost) => calculateStandardContribution(cost, 0.80, 400),
        description: 'Für Leistungen von Fremdanbietern wird bei Vorlage einer Teilnahmebestätigung ein einmaliger Finanzierungszuschuss in Höhe von 80% der entstandenen Kosten, max. aber 400,00 Euro je Kalenderjahr gewährt. (Bestandteil des Gesundheitskontos active+ mit 400 Euro/Jahr)'
    },
    'BKK SBH': {
        // Assumption: Annual maximum (250€) applies as the limit for the first course
        calculateContribution: (cost) => calculateStandardContribution(cost, 0.85, 250),
        description: 'Die BKK SBH beteiligt sich an qualitätsgesicherten Präventionskursen verschiedenster Anbieter rund um die Gesundheit. Sie erstattet 85 % der Kurskosten für bis zu zwei Kurse pro Kalenderjahr, insgesamt bis zu 250 Euro pro Jahr.'
    },
    'BKK Technoform': {
        // Assumption: Annual maximum (200€) applies as the limit for the first course
        calculateContribution: (cost) => calculateStandardContribution(cost, 0.90, 200),
        description: 'Unter bestimmten Voraussetzungen beteiligt sich die BKK Technoform an den Kosten für Präventionskurse. Man erhält bei Vorlage einer Teilnahmebestätigung für - bis zu zwei Kurse - jeweils 90 Prozent des Rechnungsbetrages - insgesamt bis zu 200 Euro pro Kalenderjahr.'
    },
    'BKK WERRA-MEISSNER': {
        calculateContribution: (cost) => calculateStandardContribution(cost, 0.80, 75),
        description: 'Bei der Teilnahme an qualifizierten Präventionskursen zahlt die BKK Werra-Meissner zweimal jährlich 80% der Kursgebühr, bis zu 75 Euro pro Kurs.'
    },
    'BKK ZF & Partner': {
        // Assumption: 100% up to 200€/2 = 100€ per course
        calculateContribution: (cost) => calculateContribution100Percent(cost, 100),
        description: '100 % Kostenübernahme gibt es für 2 qualifizierte Gesundheitskurse, bis 200 € jährlich'
    },
    'BKK24': {
        // Assumption: 100% up to 125€ per course
        calculateContribution: (cost) => calculateContribution100Percent(cost, 125),
        description: 'Für Leistungen von Fremdanbietern werden, sofern kein eigenes Angebot der BKK24 besteht, bei Vorlage einer Teilnahmebestätigung die entstandenen Kosten bis max. 125 Euro je Maßnahme in voller Höhe übernommen. Die Förderung durch die BKK24 ist auf maximal zwei Kurse pro Versicherten und Kalenderjahr begrenzt.'
    },
    'BOSCH BKK': {
        calculateContribution: (cost) => calculateContribution100Percent(cost, 80),
        description: 'Die Bosch BKK übernimmt die Kosten für 2 Gesundheitskurse pro Jahr zu 100%, bis zu 80 Euro je Kurs.'
    },
    'Continentale Betriebskrankenkasse': {
        // Assumption: Annual maximum (250€) from health account applies as the limit for the first course
        calculateContribution: (cost) => calculateContribution100Percent(cost, 250),
        description: 'Die Continentale BKK fördert pro Jahr für jeden Versicherten zwei Präventionskurse (dies ist die gesetzlich mögliche Höchstmenge). Der Zuschuss beträgt 100 Prozent pro Kurs, bis zu 250 Euro pro Jahr. (Diese Leistung ist Teil des Gesundheitskontos, bei dem pro Versicherten und Jahr insgesamt 400 Euro zur Verfügung stehen.)'
    },
    'DAK-Gesundheit': {
        // Adult rate
        calculateContribution: (cost) => calculateStandardContribution(cost, 0.80, 75),
        description: 'Die DAK-Gesundheit beteiligt sich bei zwei Präventionskursen im Jahr an den Kosten – und zwar mit 80%, maximal 75 Euro der Kursgebühren. Bei Versicherten bis zur Vollendung des 18. Lebensjahres erstattet die DAK-Gesundheit 100 % der Kosten für zwei Kurse pro Jahr.'
    },
    'Debeka BKK': {
        // Assumption: Annual maximum (150€) applies as the limit for the first course
        calculateContribution: (cost) => calculateStandardContribution(cost, 0.80, 150),
        description: 'Max. 150 EUR im Kalenderjahr (80% je Kurs, maximal 2 Kurse je Kalenderjahr) werden erstattet.'
    },
    'energie-BKK': {
        calculateContribution: (cost) => calculateContribution100Percent(cost, 80),
        description: 'Zertifizierte Kurse von Fremdanbietern werden mit 100 % der Kosten, max. 80 Euro bezuschusst. Es werden bis zu 2 Kurse je Kalenderjahr bezuschusst.'
    },
    'Heimat Krankenkasse': {
        // Assumption: 100% up to 200€/2 = 100€ per course
        calculateContribution: (cost) => calculateContribution100Percent(cost, 100),
        description: 'Versicherte der Heimat Krankenkasse können jährlich an maximal zwei Präventionsmaßnahmen teilnehmen, die bei regelmäßiger Teilnahme mit insgesamt bis zu 200 Euro bezuschusst werden.'
    },
    'hkk': {
        calculateContribution: (cost) => calculateStandardContribution(cost, 0.80, 100),
        description: 'Die hkk erstattet bis zu 2 Kurse jährlich (80 % der Kosten, max. 100 Euro je Kurs).'
    },
    'IKK classic': {
        calculateContribution: (cost) => calculateContribution100Percent(cost, 90),
        description: 'Pro Kalenderjahr können Versicherte der IKK classic an zwei zertifizierten Kursen teilnehmen. Die IKK classic erstattet 100% der Teilnahmegebühren bis maximal 90 Euro pro Kurs. Insgesamt übernimmt die IKK classic somit 180 Euro für maximal zwei qualitätsgeprüfte Gesundheitskurse pro Jahr.'
    },
    'IKK gesund plus': {
        // Adult rate (90% due to 10% co-payment)
        calculateContribution: (cost) => calculateStandardContribution(cost, 0.90, 90),
        description: 'Die IKK gesund plus bezahlt bis zu 2 Präventionsmaßnahmen pro Kalenderjahr. Für Leistungen von Fremdanbietern ist die Höhe der Erstattung abzüglich der Eigenbeteiligung von 10% ab einem Betrag von 25 Euro auf maximal 90 Euro EUR je Maßnahme begrenzt. Kinder und Jugendliche bis zum vollendeten 18. Lebensjahr erhalten eine Erstattung von 100 Prozent der Kurskosten, maximal jedoch 100,00 EUR.'
    },
    'KNAPPSCHAFT': {
        // Assumption: 100% up to 80€ per course
        calculateContribution: (cost) => calculateContribution100Percent(cost, 80),
        description: 'Die KNAPPSCHAFT erstattet je Kunden maximal zwei Kurse mit bis zu 80 Euro pro Kalenderjahr. Diese Kurse können auch gleich zum Vor- bzw. Folgejahr sein.'
    },
    'mhplus BKK': {
        calculateContribution: (cost) => calculateStandardContribution(cost, 0.80, 100),
        description: 'Die Teilnahme an Gesundheitskursen von Fremdanbietern bezuschusst die mhplus zweimal im Jahr mit 80 % der Kurskosten (max. 100,00 Euro je Kurs).'
    },
    'R+V Betriebskrankenkasse': {
        calculateContribution: (cost) => calculateContribution100Percent(cost, 100),
        description: 'Die R+V BKK bezuschusst 2 Präventionskurse pro Jahr. (100 %, max. 100€ pro Kurs)'
    },
    'Salus BKK': {
        // Adult rate
        calculateContribution: (cost) => calculateStandardContribution(cost, 0.80, 100),
        description: 'Die Salus BKK beteiligt sich bei qualitätsgeprüften und zertifizierten Kursen an 80 % der Kurskosten mit max. 100 Euro und max. 2 Kursen pro Jahr (bei Kindern ab 6 Jahren 100 %, max. 100 Euro).'
    },
    'SBK': {
        calculateContribution: (cost) => calculateStandardContribution(cost, 0.75, 80),
        description: 'Die SBK erstattet maximal zweimal pro Jahr einen Zuschuss von 75% (max. 80 Euro) für zertifizierte Gesundheitskurse. Es kann auch zweimal pro Jahr die gleiche Kursart in Anspruch genommen werden.'
    },
    'SKD BKK': {
        calculateContribution: (cost) => calculateContribution100Percent(cost, 125),
        description: 'Die Kosten für zwei zertifizierte Gesundheitskurse im Jahr werden zu 100 % erstattet, maximal aber 125 Euro je Kurs.'
    },
    'Techniker Krankenkasse': {
        // Standard case, ignores combination offers / higher subsidies
        calculateContribution: (cost) => calculateStandardContribution(cost, 0.80, 150),
        description: 'Fremdkurse werden mit 80% der Kosten und bis zu 150 Euro pro Kurs bzw. 300 Euro bei Kombinationsangeboten bezuschusst, ausgewählte Kurse auch höher. Pro Jahr sind 2 Kurse möglich.'
    },
    'IKK - Die Innovationskasse': {
        // Ignores Gymondo, takes standard subsidy for external providers
        // Assumption: 100% up to 50€ per course
        calculateContribution: (cost) => calculateContribution100Percent(cost, 50),
        description: 'Die IKK - Die Innovationskasse beteiligt sich zusätzlich pro Kalenderjahr an einem Gesundheitskurs zu den Themen: Bewegung, Ernährung, Stressbewältigung/Entspannung und Suchtmittelkonsum mit maximal 50 Euro. Zusätzlich übernimmt die IK die Kosten für Gymondo Präventionskurse zu 100 Prozent. Die zertifizierten Präventionskurse helfen dir dabei, deine Muskulatur zu stärken, Schmerzen und Haltungsfehlern entgegenzuwirken und präventiv Beschwerden vorzubeugen.'
    },
    'IKK Brandenburg und Berlin': {
        // Assumption: 100% up to 75€ per course
        calculateContribution: (cost) => calculateContribution100Percent(cost, 75),
        description: 'Innerhalb eines Kalenderjahres kann man bei der IKK BB an zwei anerkannten Kursen teilnehmen. Bei regelmäßiger Teilnahme erfolgt eine Erstattung von max. 75 Euro für den Fremdkurs.'
    }
};

